/**
 * EliasRouteGate — auto-routes elias-only users to /elias.
 *
 * Mirrors apps/web/src/components/MohapRouteGate.tsx.
 *
 * If the signed-in user is a member of one or more workspaces and EVERY
 * workspace they belong to is individual-elias edition, they should
 * never see the standard N71 context dashboard. This gate runs at the
 * App level (inside WorkspaceProvider so it has the user context but
 * outside EliasShell which handles the path-prefix work) and redirects
 * them to /elias on every render-cycle navigation away from a /elias
 * or other allowed path.
 *
 * Why it lives here vs in EliasShell:
 *   - EliasShell only mounts on /elias routes, so it can't catch a
 *     user landing on /, /context, /home, etc.
 *   - This gate intercepts ALL non-/elias nav for elias-only users.
 *
 * Allowed paths (non-/elias) for these users:
 *   - /auth, /login, /signout       — auth flow
 *   - /s/*                          — public shared-page viewer
 *   - /elias, /elias/*              — Elias surface
 * Everything else gets redirected.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const ALLOW_PATHS = ["/elias", "/s/", "/auth", "/login", "/signout", "/reset-password"];

export function EliasRouteGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // null = unknown (still loading), true = elias-only, false = has at least one non-elias workspace
  const [isEliasOnly, setIsEliasOnly] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) { setIsEliasOnly(null); return; }
    let cancelled = false;
    void (async () => {
      // Resolve via person_identities → workspace_members → workspaces
      // (mirrors what WorkspaceProvider does, but pulls settings.edition).
      const { data: identities } = await supabase
        .from("person_identities")
        .select("person_id")
        .eq("provider_user_id", user.id)
        .in("provider", ["supabase_auth", "auth"])
        .eq("active", true);
      if (cancelled) return;
      const personIds = Array.from(new Set((identities ?? []).map((r: any) => r.person_id))).filter(Boolean);
      if (personIds.length === 0) { setIsEliasOnly(false); return; }

      const { data: memberships } = await supabase
        .from("workspace_members")
        .select("workspace_id, workspaces:workspace_id(settings)")
        .in("person_id", personIds)
        .eq("is_active", true);
      if (cancelled) return;
      const editions = (memberships ?? [])
        .map((m: any) => m.workspaces?.settings?.edition ?? null)
        .filter((e: string | null) => e !== null);
      if (editions.length === 0) { setIsEliasOnly(false); return; }
      setIsEliasOnly(editions.every((e: string) => e === "individual-elias"));
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (isEliasOnly !== true) return;
    const onAllowedPath = ALLOW_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p));
    if (onAllowedPath) return;
    navigate("/elias", { replace: true });
  }, [isEliasOnly, location.pathname, navigate]);

  return null;
}
